import { useMemo, useState } from 'react';
import {
  Container,
  Title,
  Table,
  Checkbox,
  Button,
  Group,
  Text,
  Avatar,
  Loader,
  Alert,
  Card,
  Box,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconTruck,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/currency';
import api from '../../utils/api';

// Define types based on the API response
interface PurchaseUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  address?: {
    street: string;
    street2?: string;
    city: string;
    province: string;
    postalCode: string;
  };
}

interface Sticker {
  id: string;
  imageUrl: string;
  sku: string;
  name: string;
}

interface Purchase {
  id: string;
  address: {
    street: string;
    street2?: string;
    city: string;
    province: string;
    postalCode: string;
  };
  updatedAt: string;
  donate: boolean;
  priceInCents: number;
  quantity: number;
  shippingPriceInCents: number;
  size: string;
  taxInCents: number;
  totalDiscountsInCents: number;
  user: PurchaseUser;
  sticker: Sticker;
}

interface PurchasesResponse {
  purchases: Purchase[];
}

const AdminShippingPage = () => {
  const { isAdmin } = useAuth();
  const [selectedPurchases, setSelectedPurchases] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);

  // Fetch all purchases
  const { data, isLoading, error } = useQuery<PurchasesResponse>({
    queryKey: ['adminPurchases'],
    queryFn: async () => {
      const response = await api.get('/api/siteadmin/purchases');
      return response.data;
    },
  });

  // Update purchases shipped status mutation
  const updateShippedMutation = useMutation({
    mutationFn: async (purchaseIds: string[]) => {
      const response = await api.put('/api/siteadmin/purchases', {
        purchaseIds,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the purchases query
      queryClient.invalidateQueries({ queryKey: ['adminPurchases'] });
      setSelectedPurchases([]);
    },
  });

  const groupedPurchases = useMemo(() => {
    const purchaseObj: Record<string, Purchase[]> = {};

    for (const purchase of data?.purchases || []) {
      if (!purchaseObj[purchase.user.id]) {
        purchaseObj[purchase.user.id] = [];
      }
      purchaseObj[purchase.user.id].push(purchase);
    }

    return Object.keys(purchaseObj).map((userId) => ({
      userId,
      purchases: purchaseObj[userId],
    }));
  }, [data?.purchases]);

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleTogglePurchase = (purchaseId: string) => {
    setSelectedPurchases((prev) =>
      prev.includes(purchaseId)
        ? prev.filter((id) => id !== purchaseId)
        : [...prev, purchaseId]
    );
  };

  const handleToggleUser = (userId: string) => {
    setExpandedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllForUser = (purchaseIds: string[]) => {
    const allSelected = purchaseIds.every((id) =>
      selectedPurchases.includes(id)
    );

    if (allSelected) {
      // Deselect all purchases for this user
      setSelectedPurchases((prev) =>
        prev.filter((id) => !purchaseIds.includes(id))
      );
    } else {
      // Select all purchases for this user
      const newSelected = [...selectedPurchases];
      purchaseIds.forEach((id) => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setSelectedPurchases(newSelected);
    }
  };

  const handleMarkAsShipped = () => {
    if (selectedPurchases.length > 0) {
      updateShippedMutation.mutate(selectedPurchases);
    }
  };

  if (isLoading) {
    return (
      <Container size="lg" py="xl" style={{ textAlign: 'center' }}>
        <Loader size="xl" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load purchases. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title order={1} mb="lg">
        Shipping Management
      </Title>

      <Group justify="space-between" mb="md">
        <Text>{data?.purchases?.length} purchases waiting to be shipped</Text>
        <Group>
          <Button
            color="green"
            leftSection={<IconTruck size={16} />}
            onClick={handleMarkAsShipped}
            disabled={selectedPurchases?.length === 0}
            loading={updateShippedMutation.isPending}
          >
            Mark Selected as Shipped ({selectedPurchases?.length})
          </Button>
        </Group>
      </Group>

      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 80 }}></Table.Th>
            <Table.Th>Customer</Table.Th>
            <Table.Th>Shipping Address</Table.Th>
            <Table.Th>Orders</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {groupedPurchases &&
            groupedPurchases.map((group) => (
              <>
                <TitleRow
                  key={`title-${group.userId}`}
                  purchases={group.purchases}
                  userId={group.userId}
                  isExpanded={expandedUsers.includes(group.userId)}
                  onToggleExpand={() => handleToggleUser(group.userId)}
                  selectedPurchases={selectedPurchases}
                  onSelectAll={() =>
                    handleSelectAllForUser(group.purchases.map((p) => p.id))
                  }
                />
                {expandedUsers.includes(group.userId) && (
                  <Table.Tr key={`expanded-${group.userId}`}>
                    <Table.Td colSpan={5} p={0}>
                      <Box p="xs">
                        <Table withTableBorder>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: 40 }}></Table.Th>
                              <Table.Th>Product</Table.Th>
                              <Table.Th>Details</Table.Th>
                              <Table.Th>Price</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            <PurchaseGroup
                              purchases={group.purchases}
                              selectedPurchases={selectedPurchases}
                              toggleSelectedPurchase={handleTogglePurchase}
                            />
                          </Table.Tbody>
                        </Table>

                        {group.purchases.length > 1 && (
                          <Box mt="md">
                            <ShipSummary purchases={group.purchases} />
                          </Box>
                        )}
                      </Box>
                    </Table.Td>
                  </Table.Tr>
                )}
              </>
            ))}
        </Table.Tbody>
      </Table>

      {data?.purchases && data?.purchases.length === 0 && (
        <Card withBorder p="xl" mt="md">
          <Text ta="center" fw={500} size="lg">
            No purchases waiting to be shipped
          </Text>
        </Card>
      )}
    </Container>
  );
};

const TitleRow = ({
  purchases,
  userId,
  isExpanded,
  onToggleExpand,
  selectedPurchases,
  onSelectAll,
}: {
  purchases: Purchase[];
  userId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedPurchases: string[];
  onSelectAll: () => void;
}) => {
  const user = purchases[0].user;
  const purchaseIds = purchases.map((p) => p.id);
  const allSelected = purchaseIds.every((id) => selectedPurchases.includes(id));
  const someSelected =
    purchaseIds.some((id) => selectedPurchases.includes(id)) && !allSelected;

  const totalAmount = purchases.reduce(
    (sum, purchase) =>
      sum +
      purchase.priceInCents +
      purchase.shippingPriceInCents +
      purchase.taxInCents -
      purchase.totalDiscountsInCents,
    0
  );

  return (
    <Table.Tr key={userId} style={{ backgroundColor: '#f1f1f1' }}>
      <Table.Td>
        <Group>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectAll();
            }}
          />
          <Button variant="subtle" size="xs" p={0} onClick={onToggleExpand}>
            {isExpanded ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </Button>
        </Group>
      </Table.Td>
      <Table.Td>
        <Group>
          <Avatar src={user.profileImage} radius="xl" size="sm" />
          <div>
            <Text size="sm">{`${user.firstName} ${user.lastName}`}</Text>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{user.address?.street}</Text>
        {user.address?.street2 && <Text size="sm">{user.address.street2}</Text>}
        <Text size="sm">
          {user.address?.city}, {user.address?.province}{' '}
          {user.address?.postalCode}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text>
          {purchases.length} order{purchases.length > 1 ? 's' : ''}
        </Text>
        <Text>{formatCurrency(totalAmount, 'USD')}</Text>
      </Table.Td>
    </Table.Tr>
  );
};

const PurchaseGroup = ({
  purchases,
  selectedPurchases,
  toggleSelectedPurchase,
}: {
  purchases: Purchase[];
  selectedPurchases: string[];
  toggleSelectedPurchase: (purchaseId: string) => void;
}) => {
  return (
    <>
      {purchases.map((purchase) => (
        <Table.Tr key={purchase.id}>
          <Table.Td>
            <Checkbox
              checked={selectedPurchases.includes(purchase.id)}
              onChange={() => toggleSelectedPurchase(purchase.id)}
            />
          </Table.Td>
          <Table.Td>
            <Group>
              <Avatar src={purchase.sticker.imageUrl} radius="md" size="md" />
              <div>
                <Text fw={500}>{purchase.sticker.name}</Text>
                <Text size="xs" c="dimmed">
                  SKU: {purchase.sticker.sku}
                </Text>
              </div>
            </Group>
          </Table.Td>
          <Table.Td>
            <Text size="sm" fw={500}>
              Size: {purchase.size}
            </Text>
            <Text size="sm">Quantity: {purchase.quantity}</Text>
            <Text size="xs" c="dimmed">
              Order Date: {new Date(purchase.updatedAt).toLocaleDateString()}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text fw={500}>
              {formatCurrency(
                purchase.priceInCents +
                  purchase.shippingPriceInCents +
                  purchase.taxInCents -
                  purchase.totalDiscountsInCents,
                'USD'
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {formatCurrency(purchase.priceInCents, 'USD')} +{' '}
              {formatCurrency(purchase.shippingPriceInCents, 'USD')} shipping
            </Text>
          </Table.Td>
        </Table.Tr>
      ))}
    </>
  );
};

const ShipSummary = ({ purchases }: { purchases: Purchase[] }) => {
  const groupedUserPurchases = useMemo(() => {
    const items: Record<
      string,
      {
        stickerImage: string;
        stickerName: string;
        quantity: number;
        size: string;
      }
    > = {};

    for (const purchase of purchases) {
      const key = `${purchase.sticker.id}-${purchase.size}`;
      if (!items[key]) {
        items[key] = {
          stickerImage: purchase.sticker.imageUrl,
          stickerName: purchase.sticker.name,
          quantity: 0,
          size: purchase.size,
        };
      }
      items[key].quantity += purchase.quantity;
    }
    return Object.keys(items).map((x) => ({ ...items[x], key: x }));
  }, [purchases]);

  return (
    <Card withBorder mt="xs" mb="xs">
      <Text fw={500} mb="sm">
        Order Summary
      </Text>
      <Group>
        {groupedUserPurchases.map((x) => (
          <Group key={x.key}>
            <Avatar src={x.stickerImage} radius="md" size="md" />
            <Text>
              {x.stickerName} - {x.size} ({x.quantity})
            </Text>
          </Group>
        ))}
      </Group>
    </Card>
  );
};

export default AdminShippingPage;
